/**
 * The band between the phase strip and the canvas: **what the pattern below is, and how it runs.**
 *
 * ONE component for both canvases. `Builder planu - tydzien - programator`'s third row and
 * `Builder planu sekwencja`'s draw the same 44px band — `0 20px`, a hairline under it and no
 * background of its own, so it shows the canvas where the two bars above it show `--bg-card` — with
 * the same four parts in the same places. What differs is what stands inside the pill:
 *
 * · the WEEK repeats a count, so the pill holds "↻ powtarza się − ×3 + · tyg. 1–3";
 * · the SEQUENCE canvas repeats nothing countable here (a phase's cycle count is set in the phase
 *   band), so the pill holds one static sentence: "↻ zapętlony · niezależny od kalendarza".
 *
 * That is view ADR §2's "one component, the unit as a parameter" exactly. A sibling component for
 * the second row would have been two files carrying one band, differing by three bytes of geometry —
 * which is the near-duplicate the styling rules exist to stop. `PatternBarView` carries both shapes:
 * `repeat` non-null on the week, `note` non-null on the sequence canvas, never both.
 *
 * ── It is the one thing the week grid never said ─────────────────────────────────────────────
 * The grid draws a single week and the phase runs it for four; until this band, the only statement
 * of that was "tyg. 1–4" on a tab, and a trainer had to already know what the grid below it meant.
 *
 * ── The ± is the phase editor's stepper, reached from a second place ────────────────────────
 * Both ends dispatch `resize`, which is `usePhaseEditing`'s single call to `setPhaseCycles` — the
 * same operation the editor's own − / + dispatch, with the same gates
 * (`phaseEditGates`: a phase the client has trained into cannot shrink below its consumed floor,
 * and no plan may pass 52 cycles). Nothing about a phase's length is decided here.
 *
 * A refused end keeps `aria-disabled` and its reason rather than the `disabled` attribute (view
 * ADR §12), so it stays in the tab order and the explanation stays reachable. The reason is drawn as
 * ordinary text in the band rather than hidden behind `aria-describedby` alone — the correction
 * "+ Faza" took on 4 August 2026, when the only trainer who ever learned why a control was dimmed
 * was one using a screen reader.
 *
 * ── The pill is `--good`, and that is the mock's own colour rather than an accent spend ──────
 * `--good-soft` under `--good`, exactly as the mock writes it. It is a STATEMENT about the plan's
 * shape, not an action and not a warning, and the screen's four accent marks are spoken for
 * elsewhere (`pages/plan-builder/AGENTS.md`). Nothing here is colour-only: the ↻ is decorative and
 * every fact in the pill is written out in words beside it.
 */

import { useId } from 'react';

import visuallyHidden from '~/shared/ui/visuallyHidden.module.css';

import type { PatternBarView, PatternRepeatView } from '../model/builderChrome';

import styles from './PatternBar.module.css';

interface PatternBarProps {
  readonly view: PatternBarView;
  /**
   * Set the phase's cycle count. `null` on a plan that cannot be edited, and on every canvas whose
   * `view.repeat` is null — the band then states the pattern and offers nothing, rather than drawing
   * two controls that refuse for a reason nobody can act on.
   */
  readonly onResize: ((cycles: number) => void) | null;
}

/** One end of the count. Focusable while refused, carrying the reason the entity gave. */
function StepButton({
  step,
  glyph,
  reasonId,
  onResize,
}: {
  readonly step: PatternRepeatView['decrease'];
  readonly glyph: string;
  readonly reasonId: string;
  readonly onResize: (cycles: number) => void;
}) {
  if (step === null) return null;
  return (
    <button
      type="button"
      className={styles.step}
      aria-label={step.label}
      /* `undefined` rather than `false` on a live step: every other refusable control in this
       * screen omits the attribute when it can act, and `aria-disabled="false"` on a working
       * ± is one more thing for a reader to resolve than the step next to it. */
      aria-disabled={step.disabledReason === null ? undefined : 'true'}
      aria-describedby={step.disabledReason === null ? undefined : reasonId}
      onClick={() => {
        if (step.disabledReason === null) onResize(step.value);
      }}
    >
      <span aria-hidden="true">{glyph}</span>
    </button>
  );
}

export function PatternBar({ view, onResize }: PatternBarProps) {
  const shortenReasonId = useId();
  const lengthenReasonId = useId();
  const repeat = view.repeat;

  return (
    <div className={styles.bar}>
      <span className={styles.title}>{view.title}</span>

      {/* `data-static` carries the three bytes the sequence mock's pill differs by: 26px instead of
        * 28, radius 8 instead of 9, and the label at 700 instead of 600. Everything else — the
        * ground, the ink, the ↻, the 10px padding — is the same value in both mocks. */}
      <span className={styles.pill} data-static={repeat === null ? '' : undefined}>
        <span className={styles.loop} aria-hidden="true">
          ↻
        </span>

        {repeat === null ? (
          <span className={styles.repeats}>{view.note}</span>
        ) : (
          <>
            <span className={styles.repeats}>{repeat.repeatsLabel}</span>
            {onResize !== null && (
              <StepButton
                step={repeat.decrease}
                glyph="−"
                reasonId={shortenReasonId}
                onResize={onResize}
              />
            )}
            {/* An `<output>`, whose implicit `role="status"` announces the new count on every press
              * — the same element the phase editor's stepper and the gap stepper already use.
              *
              * A live region announces its CHANGED CONTENT, not its name, so the sentence has to be
              * inside it rather than on an `aria-label`: "×3" read out is "times three", which names
              * neither the unit nor the thing being counted. The digits are drawn and hidden from
              * the tree, the sentence is in the tree and drawn nowhere, and the region carries
              * exactly one of them to each reader. */}
            <output className={styles.count}>
              <span aria-hidden="true">{repeat.count}</span>
              <span className={visuallyHidden.srOnly}>{repeat.countLabel}</span>
            </output>
            {onResize !== null && (
              <StepButton
                step={repeat.increase}
                glyph="+"
                reasonId={lengthenReasonId}
                onResize={onResize}
              />
            )}
            <span className={styles.separator} aria-hidden="true">
              ·
            </span>
            <span className={styles.range}>{repeat.range}</span>
          </>
        )}
      </span>

      {/* Each end owes its OWN sentence: the floor is the client's trained cycles, the ceiling is
        * the plan-wide cap. One shared reason put the shrink refusal under a ⊕ that would have
        * worked, which is the defect `PhaseEditor` already fixed once. */}
      {repeat?.decrease?.disabledReason != null && (
        <p id={shortenReasonId} className={styles.reason}>
          {repeat.decrease.disabledReason}
        </p>
      )}
      {repeat?.increase?.disabledReason != null && (
        <p id={lengthenReasonId} className={styles.reason}>
          {repeat.increase.disabledReason}
        </p>
      )}

      <span className={styles.spacer} />
      <span className={styles.summary}>{view.summary}</span>
    </div>
  );
}
